import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, PENDING_INVITE_KEY, TOKEN_KEY } from "@/lib/api";
import { resetLook } from "@/lib/themes";
import { disablePush } from "@/lib/push";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = signed out, object = session
  const [session, setSession] = useState(null);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setSession(false);
      return false;
    }
    try {
      const { data } = await api.get("/auth/me");
      setSession(data);
      return data;
    } catch (e) {
      if (e.response?.status === 401) localStorage.removeItem(TOKEN_KEY);
      setSession(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // Returning from Google: AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) return;
    refresh();
  }, [refresh]);

  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        // Vault 401s (vault_locked / vault_bad_code) only re-lock the Vault; they never sign the person out.
        const url = String(err.config?.url);
        if (err.response?.status === 401 && !url.startsWith("/auth/") && !/\/(vault|documents)(\/|$|\?)/.test(url)) {
          localStorage.removeItem(TOKEN_KEY);
          setSession(false);
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const signIn = useCallback((data) => {
    const { token, ...rest } = data;
    localStorage.setItem(TOKEN_KEY, token);
    setSession(rest);
    return rest;
  }, []);

  const signOut = useCallback(async () => {
    await disablePush();
    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* session ends client-side regardless */
    }
    localStorage.removeItem(TOKEN_KEY);
    resetLook();
    setSession(false);
  }, []);

  return <AuthCtx.Provider value={{ session, setSession, refresh, signIn, signOut }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

let lastBack = null; // sign-in re-renders call this twice; the second call must land on the same page
export function afterLoginPath(s) {
  if (s.type === "kid") return `/h/${s.last_organization_id}/home`;
  const back = sessionStorage.getItem("qhh.returnTo");
  if (back) { sessionStorage.removeItem("qhh.returnTo"); lastBack = { p: back, t: Date.now() }; return back; }
  if (lastBack && Date.now() - lastBack.t < 5000) return lastBack.p;
  const pending = localStorage.getItem(PENDING_INVITE_KEY);
  if (pending) return `/invite/${pending}`;
  if (s.user?.platform_role) return "/admin/home";
  return s.last_organization_id ? `/h/${s.last_organization_id}/home` : "/onboarding";
}
